import { useCallback, useRef, useState } from "react";
import axios from "axios";
import {
  getOldExchangeRateSuppliers,
  getOldExchangeRateCbdFile,
} from "@/api/migrationExchangeRateService";
import { LEGACY_TOKEN_EXPIRED } from "@/api/migrationService";
import { bulkImportExchangeRateParts } from "@/api/breakdownExchangeRateService";
import { parseExchangeRatePartFromExcel } from "@/utils/excel/importPartExchangeRate";
import {
  toExchangeRateFileName,
  fileExists,
  writeXlsx,
  listXlsx,
  readAsFile,
  deleteXlsx,
  parseExchangeRateFileName,
  base64ToUint8Array,
} from "@/utils/excel/migrationFileStore";

/**
 * Batch runner สำหรับ migrate exchange rate part ระบบเก่า (admin tool, one-shot)
 * mirror ของ usePartMigration.ts แต่ 1 task = 1 supplier (ไม่ใช่ราย part × version)
 *
 * Phase A — Pull (startPull):
 *   ดึง Excel จากระบบเก่าผ่าน BE local → เขียนลงโฟลเดอร์ในเครื่อง
 *   โฟลเดอร์ = checkpoint (fileExists check) · ต้องใช้ Legacy Token · BE local ต้องเปิดอยู่
 *
 * Phase B — Import (startImport):
 *   อ่าน Excel จากโฟลเดอร์ → parse → bulk import เข้าระบบใหม่ผ่าน BE Azure (atomic ต่อ supplier)
 *   ไม่ต้องใช้ token · checkpoint ผ่าน localStorage (เฉพาะ commit mode)
 */

const CONCURRENCY = 2;
/** Phase A downloads + decodes large base64 files — keep sequential to avoid memory spikes */
const CONCURRENCY_PULL = 1;
const MAX_LOG = 200;
/** Panel active/inactive แชร์ hook เดียวกัน แต่ progress ต้องแยก — ไม่งั้นทับกัน */
const checkpointKey = (activeFilter: 0 | 1) =>
  activeFilter === 1
    ? "exchange-rate-migration-checkpoint"
    : "exchange-rate-migration-checkpoint-inactive";

export type MigrationMode = "dry-run" | "commit";
export type ItemStatus = "ok" | "warning" | "error" | "skipped";
export type MigrationPhase =
  | "idle"
  | "running"
  | "paused"
  | "need-token"
  | "done";
export type MigrationOp = "pull" | "import" | null;

export type MigrationLog = {
  supplierCode: string;
  status: ItemStatus;
  warnings: number;
  message: string;
};

export type MigrationCounters = {
  total: number;
  processed: number;
  ok: number;
  warning: number;
  error: number;
  skipped: number;
};

export type CheckpointData = {
  statusMap: Record<string, ItemStatus>;
  mode: MigrationMode;
};

const emptyCounters = (): MigrationCounters => ({
  total: 0,
  processed: 0,
  ok: 0,
  warning: 0,
  error: 0,
  skipped: 0,
});

type Task = {
  supplierCode: string;
};

type TaskOutcome =
  | { kind: "done"; log: MigrationLog }
  | { kind: "token-expired" };

type TaskProcessor = (task: Task, runMode: MigrationMode) => Promise<TaskOutcome>;

const taskKey = (t: Task) => t.supplierCode;

const loadCheckpoint = (key: string): CheckpointData | null => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed)) return null;
    return parsed as CheckpointData;
  } catch {
    return null;
  }
};

const saveCheckpoint = (
  key: string,
  statusMap: Map<string, ItemStatus>,
  mode: MigrationMode,
) => {
  const data: CheckpointData = {
    statusMap: Object.fromEntries(statusMap),
    mode,
  };
  localStorage.setItem(key, JSON.stringify(data));
};

const countsFromMap = (statusMap: Map<string, ItemStatus>) => {
  let ok = 0, warning = 0, error = 0, skipped = 0;
  for (const s of statusMap.values()) {
    if (s === "ok") ok++;
    else if (s === "warning") warning++;
    else if (s === "error") error++;
    else if (s === "skipped") skipped++;
  }
  return { ok, warning, error, skipped };
};

export const useExchangeRatePartMigration = (activeFilter: 0 | 1 = 1) => {
  const CHECKPOINT_KEY = checkpointKey(activeFilter);

  const [phase, setPhase] = useState<MigrationPhase>("idle");
  const [mode, setMode] = useState<MigrationMode>("dry-run");
  const [currentOp, setCurrentOp] = useState<MigrationOp>(null);
  const [counters, setCounters] = useState<MigrationCounters>(emptyCounters());
  const [log, setLog] = useState<MigrationLog[]>([]);
  const [dirName, setDirName] = useState<string | null>(null);

  const stopRef = useRef(false);
  const needTokenRef = useRef(false);
  const modeRef = useRef<MigrationMode>("dry-run");
  const legacyTokenRef = useRef("");
  const tasksRef = useRef<Task[]>([]);
  const statusMapRef = useRef<Map<string, ItemStatus>>(new Map());
  const resultsMapRef = useRef<Map<string, MigrationLog>>(new Map());
  const saveCounterRef = useRef(0);
  const processorRef = useRef<TaskProcessor | null>(null);
  const runConcurrencyRef = useRef(CONCURRENCY);
  const runSkipCheckpointRef = useRef(false);
  const logBufferRef = useRef<MigrationLog[]>([]);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushState = useCallback(() => {
    flushTimerRef.current = null;
    const { ok, warning, error, skipped } = countsFromMap(statusMapRef.current);
    setCounters((c) => ({
      ...c,
      processed: ok + warning + error + skipped,
      ok,
      warning,
      error,
      skipped,
    }));
    setLog([...logBufferRef.current]);
  }, []);

  const record = useCallback((entry: MigrationLog) => {
    const key = entry.supplierCode;
    statusMapRef.current.set(key, entry.status);

    const isPhaseA = runSkipCheckpointRef.current;
    const shouldRecord = !isPhaseA || entry.status === "error";

    if (shouldRecord) {
      resultsMapRef.current.set(key, entry);
      logBufferRef.current = [entry, ...logBufferRef.current];
      if (logBufferRef.current.length > MAX_LOG) {
        logBufferRef.current = logBufferRef.current.slice(0, MAX_LOG);
      }
    }

    if (!flushTimerRef.current) {
      flushTimerRef.current = setTimeout(flushState, 150);
    }
  }, [flushState]);

  // ─── Core loop (processor injected via processorRef) ─────────────────────────

  const runLoop = useCallback(async (runMode: MigrationMode) => {
    stopRef.current = false;
    needTokenRef.current = false;
    saveCounterRef.current = 0;
    setPhase("running");

    const concurrency = runConcurrencyRef.current;
    const skipCheckpoint = runSkipCheckpointRef.current;

    const pending = tasksRef.current.filter((t) => {
      const s = statusMapRef.current.get(taskKey(t));
      return s === undefined || s === "error";
    });

    let cursor = 0;
    const worker = async () => {
      while (cursor < pending.length && !stopRef.current) {
        const task = pending[cursor++];
        const outcome = await processorRef.current!(task, runMode);
        if (outcome.kind === "token-expired") {
          needTokenRef.current = true;
          stopRef.current = true;
          return;
        }
        record(outcome.log);
        if (!skipCheckpoint && outcome.log.status !== "error") {
          saveCounterRef.current++;
          if (saveCounterRef.current % 50 === 0) {
            saveCheckpoint(CHECKPOINT_KEY, statusMapRef.current, runMode);
          }
        }
      }
    };

    await Promise.all(Array.from({ length: concurrency }, () => worker()));
    if (!skipCheckpoint) {
      saveCheckpoint(CHECKPOINT_KEY, statusMapRef.current, runMode);
    }

    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushState();
    }

    if (needTokenRef.current) setPhase("need-token");
    else if (stopRef.current) setPhase("paused");
    else setPhase("done");
  }, [record, flushState, CHECKPOINT_KEY]);

  // ─── Phase A: Pull to disk ────────────────────────────────────────────────────

  const startPull = useCallback(
    async (dir: FileSystemDirectoryHandle) => {
      processorRef.current = async (task): Promise<TaskOutcome> => {
        const base = { supplierCode: task.supplierCode };
        try {
          const name = toExchangeRateFileName(task.supplierCode);
          if (await fileExists(dir, name)) {
            return { kind: "done", log: { ...base, status: "skipped", warnings: 0, message: "มีอยู่แล้ว" } };
          }
          const { fileArray } = await getOldExchangeRateCbdFile(task.supplierCode, legacyTokenRef.current);
          const bytes = base64ToUint8Array(fileArray);
          await writeXlsx(dir, name, bytes);
          return { kind: "done", log: { ...base, status: "ok", warnings: 0, message: name } };
        } catch (err) {
          const detail = axios.isAxiosError(err)
            ? (err.response?.data as { detail?: string } | undefined)?.detail
            : undefined;
          if (detail === LEGACY_TOKEN_EXPIRED) return { kind: "token-expired" };
          if (axios.isAxiosError(err) && err.response?.status === 404) {
            return { kind: "done", log: { ...base, status: "skipped", warnings: 0, message: "ไม่มีข้อมูล" } };
          }
          const message = detail ? String(detail) : err instanceof Error ? err.message : "unknown error";
          return { kind: "done", log: { ...base, status: "error", warnings: 0, message } };
        }
      };

      setCurrentOp("pull");
      setDirName(dir.name);
      modeRef.current = "dry-run";

      setPhase("running");
      const refs = await getOldExchangeRateSuppliers();
      tasksRef.current = refs.map((r) => ({ supplierCode: r.supplier_code }));

      // dir เป็น checkpoint ของตัวเอง (fileExists check) ไม่ seed จาก localStorage
      statusMapRef.current = new Map();
      resultsMapRef.current = new Map();
      logBufferRef.current = [];
      runConcurrencyRef.current = CONCURRENCY_PULL;
      runSkipCheckpointRef.current = true;
      setLog([]);
      setCounters({ ...emptyCounters(), total: tasksRef.current.length });

      await runLoop("dry-run");
    },
    [runLoop],
  );

  // ─── Phase B: Import from disk ────────────────────────────────────────────────

  const startImport = useCallback(
    async (dir: FileSystemDirectoryHandle, runMode: MigrationMode) => {
      processorRef.current = async (task, rm): Promise<TaskOutcome> => {
        const base = { supplierCode: task.supplierCode };
        try {
          const name = toExchangeRateFileName(task.supplierCode);
          const file = await readAsFile(dir, name);
          const parsed = await parseExchangeRatePartFromExcel(file, task.supplierCode, activeFilter);

          // ทุก error (part/material code หาไม่เจอ) บล็อกทั้ง supplier — ไม่มี partial import
          // (process หาไม่เจอไม่ error อีกแล้ว — backend auto-create process master ให้)
          if (parsed.errors.length > 0) {
            const msgs = [...new Set(parsed.errors.map((e) => e.message))];
            return { kind: "done", log: { ...base, status: "error", warnings: 0, message: msgs.join(", ") } };
          }

          if (rm === "commit") {
            const body = parsed.rows.map((r) => ({
              part_id: r.part_id,
              exchange_rate_supplier_id: r.exchange_rate_supplier_id,
              currency_id: r.currency_id,
              currency_code: r.currency_code,
              price: r.price,
              is_active: activeFilter === 1,
              oem_other_process: r.oem_other_process,
              spare_other_process: r.spare_other_process,
            }));
            await bulkImportExchangeRateParts(task.supplierCode, body);
            await deleteXlsx(dir, name);
          }
          return { kind: "done", log: { ...base, status: "ok", warnings: 0, message: "" } };
        } catch (err) {
          if (axios.isAxiosError(err) && err.response?.status === 409) {
            await deleteXlsx(dir, toExchangeRateFileName(task.supplierCode));
            return { kind: "done", log: { ...base, status: "skipped", warnings: 0, message: "Supplier มีอยู่แล้ว — ลบไฟล์แล้ว" } };
          }
          const message = err instanceof Error ? err.message : "unknown error";
          return { kind: "done", log: { ...base, status: "error", warnings: 0, message } };
        }
      };

      setCurrentOp("import");
      setDirName(dir.name);
      modeRef.current = runMode;
      setMode(runMode);

      const files = await listXlsx(dir);
      const tasks: Task[] = files.flatMap((name) => {
        const parsed = parseExchangeRateFileName(name);
        if (!parsed) return [];
        return [{ supplierCode: parsed.supplierCode }];
      });
      tasksRef.current = tasks;

      // commit mode: seed จาก checkpoint เดิม (skip รายการที่ import แล้ว)
      const statusMap = new Map<string, ItemStatus>();
      if (runMode === "commit") {
        const cp = loadCheckpoint(CHECKPOINT_KEY);
        if (cp?.mode === "commit") {
          for (const [k, s] of Object.entries(cp.statusMap)) {
            statusMap.set(k, s as ItemStatus);
          }
        }
      }
      statusMapRef.current = statusMap;
      resultsMapRef.current = new Map();
      logBufferRef.current = [];
      runConcurrencyRef.current = CONCURRENCY;
      runSkipCheckpointRef.current = false;
      setLog([]);

      const { ok, warning, skipped } = countsFromMap(statusMap);
      setCounters({
        ...emptyCounters(),
        total: tasks.length,
        processed: ok + warning + skipped,
        ok,
        warning,
        skipped,
      });

      await runLoop(runMode);
    },
    [runLoop, activeFilter, CHECKPOINT_KEY],
  );

  const resume = useCallback(
    async (newToken?: string) => {
      if (newToken) legacyTokenRef.current = newToken;
      await runLoop(modeRef.current);
    },
    [runLoop],
  );

  const stop = useCallback(() => {
    stopRef.current = true;
  }, []);

  const setLegacyToken = useCallback((token: string) => {
    legacyTokenRef.current = token;
  }, []);

  const resetCheckpoint = useCallback(() => {
    statusMapRef.current = new Map();
    resultsMapRef.current = new Map();
    logBufferRef.current = [];
    localStorage.removeItem(CHECKPOINT_KEY);
    setLog([]);
    setCounters(emptyCounters());
    setPhase("idle");
  }, [CHECKPOINT_KEY]);

  const downloadReport = useCallback(() => {
    const rows = [
      "supplier_code,status,warnings,message",
      ...[...resultsMapRef.current.values()].map(
        (r) =>
          `${r.supplierCode},${r.status},${r.warnings},"${r.message.replace(/"/g, "'")}"`,
      ),
    ];
    const blob = new Blob(["﻿" + rows.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `exchange-rate-migration-report-${mode}-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [mode]);

  return {
    phase,
    mode,
    currentOp,
    dirName,
    counters,
    log,
    startPull,
    startImport,
    resume,
    stop,
    setLegacyToken,
    resetCheckpoint,
    downloadReport,
  };
};
